import { sendMetrics } from "@expert/cloudwatch-metrics";

import type { ChatMessage } from "@/sdk/agent/chat";

/**
 * Records customer→expert chat message latency: from the trustworthy server-side time our
 * backend stamped when it received the customer's message (`message.serverReceivedTimestamp`,
 * sourced from `metadata.serverReceivedTimestamp` on Amazon Connect's `ChatEventData.Content`)
 * to the moment we observe the message on our ChatJS websocket. Feeds the customer-to-expert
 * panel on the Expert Workspace Health dashboard.
 *
 * Uses the backend receive time rather than the customer's client-side clock, which can be
 * skewed or manipulated.
 *
 * Emitted in seconds (`unit: "s"`) to line up with the outbound backend metric
 * (`exwo_chat_outbound_message_e2e_seconds_bucket`) and to preserve tail-latency resolution
 * beyond 10 s — the default histogram boundaries treated as seconds cover multi-minute
 * stalls, whereas an `ms` unit would cap explicit buckets at 10 s and dump everything
 * larger into `+Inf`.
 *
 * Fire-and-forget. Safe to call unconditionally once per fully-processed inbound message —
 * the function itself gates emission on all five of:
 *   1. `message.senderType === "customer"` — only customer-sent messages contribute
 *   2. `message.receiptStatus === "delivered"` — filters out messages that already carry a
 *      read receipt from another expert (multi-expert handoff scenario)
 *   3. `message.serverReceivedTimestamp` present (payload carries the backend receive time).
 *      Backfilled history messages (pre-existing bot transcript re-published to Connect when
 *      a Simplr chat escalates to us) are created via a different backend path that does not
 *      stamp this field, so they are naturally excluded here.
 *   4. `serverReceivedTimestamp >= chatAssignedAt` — defense-in-depth against any backfilled
 *      message that predates the expert joining the chat. Skipped only when `chatAssignedAt`
 *      is passed; unset defaults to permissive.
 *   5. resulting duration is finite and non-negative (guards against clock skew between the
 *      backend and the browser)
 *
 * `chatAssignedAt` is passed via `context` because it does not live on `ChatMessage` — it
 * comes from the task/chat state.
 */
export function recordInboundCustomerMessageLatency(
    { senderType, receiptStatus, serverReceivedTimestamp }: ChatMessage,
    context?: { chatAssignedAt?: Date },
): void {
    if (senderType !== "customer") return;
    if (receiptStatus !== "delivered") return;
    if (!serverReceivedTimestamp) return;

    const startMs = new Date(serverReceivedTimestamp).getTime();
    if (context?.chatAssignedAt && startMs < context.chatAssignedAt.getTime()) return;

    const durationMs = Date.now() - startMs;
    if (!Number.isFinite(durationMs) || durationMs < 0) return;

    void sendMetrics([
        {
            metrics: [{ name: "Duration", value: Math.round(durationMs) / 1000, unit: "s" }],
            dimensions: {
                actionName: "chat_inbound_message_e2e_latency",
            },
        },
    ]);
}
